import { Head } from "@inertiajs/react"
import { Check, Clock, Zap, Bot, Database, Mail, FileText, TrendingUp, Repeat } from "lucide-react"
import { CompanyLayout } from "~/components/company/CompanyLayout"
import { ServicePageCTA } from "~/components/company/ServicePageCTA"
import { ContactSection } from "~/components/company/sections/ContactSection"
import { HeroAutomationWorkbench } from "~/components/company/sections/HeroAutomationWorkbench"
import { SectionReveal, StaggerReveal, StaggerItem, FadeScale } from "~/components/company/SectionReveal"

export default function AIWorkflowAutomationPage() {
  return (
    <CompanyLayout>
      <Head>
        <title>AI Workflow Automation | Optimal Dev Labs</title>
        <meta name="description" content="Automate repetitive work and 10x your team with custom AI agents and workflow automations. Built for your specific processes, not generic templates. Projects start at $5k." />
        <meta name="keywords" content="ai workflow automation, custom ai agents, business process automation, workflow automation agency, ai integration, operations automation, automate repetitive tasks, ai for business" />
        <meta property="og:title" content="AI Workflow Automation | Optimal Dev Labs" />
        <meta property="og:description" content="Automate repetitive work and 10x your team with custom AI agents and workflow automations. Projects start at $5k." />
        <meta property="og:url" content="https://optimaldevlabs.com/services/ai-workflow-automation" />
        <meta name="twitter:title" content="AI Workflow Automation | Optimal Dev Labs" />
        <meta name="twitter:description" content="Automate repetitive work and 10x your team with custom AI agents. Projects start at $5k." />
        <link rel="canonical" href="https://optimaldevlabs.com/services/ai-workflow-automation" />
      </Head>
      <main className="flex-1">
        <section className="w-full bg-white py-12 md:py-20 lg:py-28">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-center max-w-6xl mx-auto">
              <SectionReveal className="flex flex-col space-y-6 text-center lg:text-left lg:flex-1">
                <div className="space-y-4">
                  <p className="text-[#ed2132] font-semibold text-sm">AI WORKFLOW AUTOMATION</p>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight tracking-tight text-balance">
                    10x Your Team with AI
                  </h1>
                  <p className="text-lg sm:text-xl md:text-2xl text-slate-600 max-w-xl mx-auto lg:mx-0 text-balance">
                    Automate repetitive work. Custom AI agents for your specific processes. Hours back in your day.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                  <ServicePageCTA label="Book a Discovery Call" />
                </div>

                <p className="text-sm text-slate-500">
                  Projects start at $5k. ROI typically measured in weeks.
                </p>
              </SectionReveal>

              <SectionReveal delay={0.2} direction="right" className="w-full lg:flex-1 flex justify-center">
                <HeroAutomationWorkbench />
              </SectionReveal>
            </div>
          </div>
        </section>

        <section className="w-full bg-white py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <SectionReveal className="max-w-4xl mx-auto">
              <div className="space-y-3 text-center">
                <p className="text-[#ed2132] font-semibold">WHO THIS IS FOR</p>
                <p className="text-lg text-slate-600">
                  Operations leaders and teams doing repetitive manual work. Teams with $5k-$20k budget for automation projects that can show ROI in weeks.
                </p>
              </div>
            </SectionReveal>
          </div>
        </section>

        <section className="w-full bg-slate-50 py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-10">
              <SectionReveal className="space-y-4 text-center">
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  Your Team Is Doing Work That Should Be Automated
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  You know there&apos;s a better way. You just don&apos;t have time to build it.
                </p>
              </SectionReveal>

              <StaggerReveal className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  {
                    icon: Clock,
                    title: "Hours Lost to Manual Work",
                    description: "Data entry, report generation, email responses, document processing. Eating up your team's time every day."
                  },
                  {
                    icon: Database,
                    title: "Data Scattered Everywhere",
                    description: "10+ tools that don't talk to each other. Copy-pasting between systems. Information silos."
                  },
                  {
                    icon: Repeat,
                    title: "Same Tasks, Over and Over",
                    description: "Repetitive processes that follow the same pattern every time. Perfect for automation, if you had time."
                  },
                  {
                    icon: TrendingUp,
                    title: "Scaling Means More Headcount",
                    description: "To grow, you hire more people to do more manual work. That's not sustainable."
                  }
                ].map((problem, idx) => {
                  const Icon = problem.icon
                  return (
                    <StaggerItem key={idx}>
                      <div className="flex gap-4 bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md hover:border-slate-300 transition-all duration-300">
                        <div className="flex-shrink-0">
                          <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-red-100">
                            <Icon className="h-5 w-5 text-[#ed2132]" />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <h3 className="text-lg font-semibold text-slate-900">{problem.title}</h3>
                          <p className="text-slate-600 text-sm">{problem.description}</p>
                        </div>
                      </div>
                    </StaggerItem>
                  )
                })}
              </StaggerReveal>
            </div>
          </div>
        </section>

        <section className="w-full bg-white py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-10">
              <SectionReveal className="space-y-4 text-center">
                <p className="text-[#ed2132] font-semibold">THE SOLUTION</p>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  Custom AI Workflows Built for Your Business
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  We build AI agents and automations that fit your exact processes, not generic templates.
                </p>
              </SectionReveal>

              <StaggerReveal className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    icon: Bot,
                    title: "AI Agents",
                    description: "Custom AI assistants that handle tasks autonomously. Email triage, document processing, data analysis."
                  },
                  {
                    icon: Zap,
                    title: "Workflow Automation",
                    description: "Connect your tools. Automate multi-step processes. Eliminate manual handoffs."
                  },
                  {
                    icon: Database,
                    title: "Data Integration",
                    description: "Unify your data sources. Real-time syncing. Single source of truth."
                  }
                ].map((solution, idx) => {
                  const Icon = solution.icon
                  return (
                    <StaggerItem key={idx}>
                      <div className="space-y-4 text-center group">
                        <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-red-100 mx-auto transition-transform duration-300 group-hover:scale-110">
                          <Icon className="h-6 w-6 text-[#ed2132]" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900">{solution.title}</h3>
                        <p className="text-slate-600 text-sm">{solution.description}</p>
                      </div>
                    </StaggerItem>
                  )
                })}
              </StaggerReveal>
            </div>
          </div>
        </section>

        <section className="w-full bg-slate-50 py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-10">
              <SectionReveal className="space-y-4 text-center">
                <p className="text-[#ed2132] font-semibold">USE CASES</p>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  What We Automate
                </h2>
              </SectionReveal>

              <StaggerReveal className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.1}>
                {[
                  {
                    icon: Mail,
                    title: "Email and Communication",
                    examples: ["Auto-categorize and route emails", "Draft responses using AI", "Meeting scheduling automation"]
                  },
                  {
                    icon: FileText,
                    title: "Document Processing",
                    examples: ["Extract data from PDFs and invoices", "Auto-generate reports", "Contract analysis and summarization"]
                  },
                  {
                    icon: Database,
                    title: "Data Operations",
                    examples: ["CRM data enrichment", "Cross-platform syncing", "Automated data entry from forms"]
                  },
                  {
                    icon: TrendingUp,
                    title: "Sales and Marketing",
                    examples: ["Lead scoring and qualification", "Personalized outreach at scale", "Campaign performance reporting"]
                  },
                  {
                    icon: Zap,
                    title: "Lead Response Automation",
                    examples: ["Respond to leads within 5 minutes", "80% higher qualification rate", "Capture the 'golden window' before competitors"]
                  }
                ].map((useCase, idx) => {
                  const Icon = useCase.icon
                  return (
                    <StaggerItem key={idx}>
                      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4 hover:shadow-md hover:border-slate-300 transition-all duration-300">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-red-100">
                            <Icon className="h-5 w-5 text-[#ed2132]" />
                          </div>
                          <h3 className="text-lg font-semibold text-slate-900">{useCase.title}</h3>
                        </div>
                        <ul className="space-y-2">
                          {useCase.examples.map((example, exIdx) => (
                            <li key={exIdx} className="flex items-center gap-2 text-sm text-slate-600">
                              <span className="text-[#ed2132]">•</span>
                              {example}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </StaggerItem>
                  )
                })}
              </StaggerReveal>
            </div>
          </div>
        </section>

        <section className="w-full bg-white py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <FadeScale className="max-w-3xl mx-auto">
              <div className="bg-gradient-to-br from-red-50 to-red-100/50 border border-red-200 rounded-lg p-8 md:p-12 space-y-6 hover:shadow-lg transition-shadow duration-500">
                <SectionReveal className="space-y-3" delay={0.1}>
                  <p className="text-[#ed2132] font-semibold">WHY SPEED MATTERS</p>
                  <h3 className="text-2xl md:text-3xl font-bold text-slate-900">
                    The Golden Window for Lead Response
                  </h3>
                </SectionReveal>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-lg font-semibold text-slate-900">
                      After 5 minutes, lead qualification drops by 80%
                    </p>
                    <p className="text-slate-600">
                      The first 5 minutes are the critical window. Most teams miss it.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                    <div className="bg-white rounded-lg p-4 space-y-2">
                      <p className="text-sm text-slate-600">Response at 5 minutes vs 30 minutes</p>
                      <p className="text-2xl font-bold text-[#ed2132]">100x more effective</p>
                      <p className="text-xs text-slate-500">for establishing connection</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 space-y-2">
                      <p className="text-sm text-slate-600">5-minute vs 30-minute response</p>
                      <p className="text-2xl font-bold text-[#ed2132]">21x better</p>
                      <p className="text-xs text-slate-500">for lead qualification</p>
                    </div>
                  </div>

                  <p className="text-slate-600 pt-4">
                    AI-powered lead response automation ensures your team captures every opportunity in that critical window, even when they&apos;re focused on closing deals or building product.
                  </p>

                  <p className="text-xs text-slate-500">
                    Source:{" "}
                    <a
                      href="https://25649.fs1.hubspotusercontent-na2.net/hub/25649/file-13535879-pdf/docs/mit_study.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2 hover:text-[#ed2132] focus-visible:text-[#ed2132] transition-colors"
                    >
                      MIT Lead Response Management Study (PDF)
                    </a>
                  </p>
                </div>
              </div>
            </FadeScale>
          </div>
        </section>

        <section className="w-full bg-white py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-10">
              <SectionReveal className="space-y-4 text-center">
                <p className="text-[#ed2132] font-semibold">HOW IT WORKS</p>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                  From Bottleneck to Breakthrough
                </h2>
              </SectionReveal>

              <StaggerReveal className="grid grid-cols-1 md:grid-cols-4 gap-6" staggerDelay={0.1}>
                {[
                  {
                    step: "01",
                    title: "Discovery",
                    description: "We map your current processes and identify automation opportunities."
                  },
                  {
                    step: "02",
                    title: "Design",
                    description: "We architect the solution: which tools, what AI, how it integrates."
                  },
                  {
                    step: "03",
                    title: "Build",
                    description: "We build and test the automation. You see working demos weekly."
                  },
                  {
                    step: "04",
                    title: "Deploy",
                    description: "We roll out, train your team, and optimize based on real usage."
                  }
                ].map((phase, idx) => (
                  <StaggerItem key={idx}>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 space-y-3 hover:shadow-lg hover:border-red-300 transition-all duration-300">
                      <p className="text-[#ed2132] text-sm font-bold">STEP {phase.step}</p>
                      <h3 className="text-lg font-bold text-slate-900">{phase.title}</h3>
                      <p className="text-slate-600 text-sm">{phase.description}</p>
                    </div>
                  </StaggerItem>
                ))}
              </StaggerReveal>
            </div>
          </div>
        </section>

        <section className="w-full bg-slate-50 py-12 md:py-16 lg:py-20">
          <div className="container px-4 md:px-6 lg:px-8">
            <FadeScale className="max-w-3xl mx-auto">
              <div className="bg-white border border-slate-200 rounded-xl p-8 md:p-12 space-y-8 text-center shadow-sm hover:shadow-xl transition-shadow duration-500">
                <div className="space-y-4">
                  <p className="text-[#ed2132] font-semibold">INVESTMENT</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
                    Typical Project Cost
                  </h2>
                </div>

                <div className="space-y-2">
                  <p className="text-5xl md:text-6xl font-bold text-[#ed2132]">
                    $5k-$20k
                  </p>
                  <p className="text-slate-600">
                    Depending on complexity and number of integrations.
                  </p>
                </div>

                <div className="space-y-4 text-left max-w-md mx-auto">
                  <p className="text-sm text-slate-600">
                    <strong>ROI typically realized within:</strong>
                  </p>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <span className="text-slate-400">•</span>
                      Simple automations: 2-4 weeks
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-slate-400">•</span>
                      Complex workflows: 1-2 months
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-slate-400">•</span>
                      Enterprise integrations: 2-3 months
                    </li>
                  </ul>
                </div>

                <div className="pt-4">
                  <ServicePageCTA label="Get a Custom Quote" />
                </div>

                <p className="text-sm text-slate-500">
                  We&apos;ll calculate your expected ROI before you commit.
                </p>
              </div>
            </FadeScale>
          </div>
        </section>

        <ContactSection
          title="Ready to Automate Your Workflows?"
          description="Tell us where your team loses the most time. We'll find the bottlenecks and calculate your expected ROI before you commit."
          inquiryPrompt="What would you like to automate?"
          inquiryOptions={[
            { value: "lead-response", label: "Lead response and follow-up" },
            { value: "email", label: "Email and communication" },
            { value: "documents", label: "Document processing and reporting" },
            { value: "data-ops", label: "Data entry and syncing between tools" },
            { value: "ai-agent", label: "A custom AI agent" },
            { value: "other", label: "Not sure yet - help me find the bottlenecks" },
          ]}
          service="AI Workflow Automation"
        />
      </main>
    </CompanyLayout>
  )
}
